// TEMPORARY control for the public-registration window.
//
// While open, anyone with the site link can create an account without an
// invitation (see server services/openRegistration.ts). The state is persisted
// server-side, so it stays put until the author closes it. Cleanup = delete this
// file and its <OpenRegistrationCard /> usage in PeoplePage, plus the server
// pieces noted in services/openRegistration.ts.
import LockOpenIcon from '@mui/icons-material/LockOpen'
import { Box, Button, Typography } from '@mui/material'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'

import { PublicRegistrationStatus, apiService } from '../../services/api'
import { logger } from '../../utils/logger'

const cardSx = {
  mb: 2,
  p: 1.5,
  borderRadius: 2,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 1.5,
} as const

const OpenRegistrationCard: React.FC = () => {
  const [status, setStatus] = useState<PublicRegistrationStatus | null>(null)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    apiService
      .getPublicRegistration()
      .then(setStatus)
      .catch((error) => logger.error('Failed to load registration status:', error))
  }, [])

  const setOpen = async (open: boolean) => {
    setSubmitting(true)
    try {
      const next = await apiService.setPublicRegistration(open)
      setStatus(next)
      toast.success(
        open ? 'Registration opened — anyone can join now' : 'Registration closed — invitation required again',
      )
    } catch (error) {
      logger.error('Failed to toggle public registration:', error)
      toast.error('Could not update registration')
    } finally {
      setSubmitting(false)
    }
  }

  if (!status) {
    return null
  }

  if (!status.open) {
    return (
      <Box sx={{ ...cardSx, border: '1px solid var(--border-subtle)', bgcolor: 'var(--bg-surface)' }}>
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>
            Public registration is closed
          </Typography>
          <Typography sx={{ fontSize: '0.8rem', color: 'var(--text-tertiary)', mt: 0.25 }}>
            New people can only join through an invitation. Open it to let anyone with the site link create an account
            without one.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          disabled={submitting}
          onClick={() => setOpen(true)}
          startIcon={<LockOpenIcon sx={{ fontSize: 18 }} />}
          sx={{ flexShrink: 0, textTransform: 'none' }}
        >
          Open registration
        </Button>
      </Box>
    )
  }

  const openedLabel = status.openedAt
    ? new Date(status.openedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : null

  return (
    <Box sx={{ ...cardSx, border: '1px solid var(--encrypt-marked-border)', bgcolor: 'var(--encrypt-marked-bg)' }}>
      <LockOpenIcon sx={{ color: 'var(--encrypt-marked-border)', fontSize: 24, flexShrink: 0 }} />
      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text-primary)' }}>
          Public registration is OPEN
        </Typography>
        <Typography sx={{ fontSize: '0.8rem', color: 'var(--text-secondary)', mt: 0.25 }}>
          Anyone with the site link can create an account without an invitation
          {openedLabel ? ` · open since ${openedLabel}` : ''}. Close it when you're done.
        </Typography>
      </Box>
      <Button
        variant="contained"
        size="small"
        disabled={submitting}
        onClick={() => setOpen(false)}
        sx={{
          flexShrink: 0,
          textTransform: 'none',
          bgcolor: 'var(--encrypt-marked-border)',
          color: '#fff',
          '&:hover': { bgcolor: 'var(--encrypt-marked-hover-border)' },
        }}
      >
        Close now
      </Button>
    </Box>
  )
}

export default OpenRegistrationCard
